import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import { GraphDotColor } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Legend,
  InfoButton,
  FieldSet,
  SelectButtons,
  SelectButton,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import { useCallback } from "react"

import styles from "./Display.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useInputPropsForValue } from "src/hooks"

const polyglotPrefix = "pages.settings.display.dot_color."

interface Props {
  updatedAt?: string;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => void;
}

// note this matches the defaults in Graph/GlucoseReading.tsx
export function DotColor(props: Props) {
  const {
    updatedAt,
    updateDeviceSettings,
  } = props

  const polyglot = usePolyglot()
  const { currentDeviceSettings } = useFollowedUsers()
  const currentDotColor = currentDeviceSettings.dot_color

  const adapter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => e.target.value as GraphDotColor,
    [],
  )

  const onValueChange = useCallback(
    (dotColor: DeviceSettings["dot_color"]) => {
      updateDeviceSettings({
        dot_color: dotColor,
      })
    },
    [updateDeviceSettings],
  )

  const {
    value,
    onChange,
  } = useInputPropsForValue({
    value: currentDotColor,
    onChange: onValueChange,
    adapter,
    updatedAt,
  })

  return (
    <FieldSet>
      <Legend id="dot-color-segmented-control">
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}label`)}>
          <p>
            {polyglot.t(`${polyglotPrefix}description_summary`)}
          </p>
          <p>
            {polyglot.t(`${polyglotPrefix}description_theme`)}
          </p>
          <p>
            {polyglot.t(`${polyglotPrefix}description_readings`)}
          </p>
        </InfoButton>
      </Legend>
      <SelectButtons className={styles.selectContainer}>
        <SelectButton
          htmlFor="graph-dot-color-white"
          name="graph-dot-color"
          value={GraphDotColor.Default}
          checked={
            value !== GraphDotColor.ReadingBased
          }
          onChange={onChange}
          id="theme"
        >
          {polyglot.t(`${polyglotPrefix}options.white`)}
        </SelectButton>
        <SelectButton
          htmlFor="graph-dot-color-reading-based"
          name="graph-dot-color"
          value={GraphDotColor.ReadingBased}
          checked={value === GraphDotColor.ReadingBased}
          onChange={onChange}
          id="readings"
        >
          {polyglot.t(`${polyglotPrefix}options.reading_based`)}
        </SelectButton>
      </SelectButtons>
    </FieldSet>
  )
}
